<script lang="ts">
  import { ALL_SQUARES, FILES, RANKS, geometricMoves, type Color, type Piece, type Square } from '@blind-chess/shared';
  import { pieceGlyph } from './pieces.js';
  import { phantomDrag } from './stores/phantom-drag.svelte.js';

  interface Props {
    pieces: Partial<Record<Square, Piece>>;
    you: Color;
    toMove: Color;
    mode: 'blind' | 'vanilla';
    highlightingEnabled: boolean;
    phantoms?: Partial<Record<Square, Piece>>;
    phantomsEnabled?: boolean;
    armedSquare: Square | null;       // local visual arm (pre-commit)
    touchedSquare: Square | null;     // server-authoritative touch
    onArm: (sq: Square | null) => void;
    onCommit: (from: Square, to: Square) => void;
  }

  let {
    pieces, you, toMove, mode, highlightingEnabled,
    phantoms = {}, phantomsEnabled = false,
    armedSquare, touchedSquare, onArm, onCommit,
  }: Props = $props();

  const ranksDisplay = $derived(you === 'w' ? [...RANKS].reverse() : [...RANKS]);
  const filesDisplay = $derived(you === 'w' ? [...FILES] : [...FILES].reverse());

  const ownSquares = $derived(
    new Set<Square>(
      Object.entries(pieces)
        .filter(([_, p]) => p?.color === you)
        .map(([sq]) => sq as Square),
    ),
  );

  // Highlight set: only when toggle is ON, only for the active piece (touched
  // wins over armed). Geometric in blind mode (no opponent input). In vanilla
  // mode we'd ideally use chess.js legal moves, but for simplicity we use the
  // same geometric set with the understanding that the client is non-authoritative.
  const highlightFrom = $derived(touchedSquare ?? armedSquare);
  const highlights = $derived.by(() => {
    if (!highlightingEnabled || !highlightFrom) return new Set<Square>();
    const piece = pieces[highlightFrom];
    if (!piece) return new Set<Square>();
    const moves = geometricMoves(piece, highlightFrom, ownSquares);
    return new Set(moves);
  });

  // The board square a phantom is currently being dragged out of (so it can
  // be dimmed while the drag ghost is shown). Bind `active` to a local first
  // so TypeScript narrows the discriminated union reliably.
  const dragOrigin = $derived.by(() => {
    const a = phantomDrag.state.active;
    return a?.kind === 'board' && phantomDrag.state.moved ? a.from : null;
  });

  function squareColor(sq: Square): 'light' | 'dark' {
    const f = sq.charCodeAt(0) - 'a'.charCodeAt(0);
    const r = parseInt(sq[1]!, 10) - 1;
    return (f + r) % 2 === 0 ? 'dark' : 'light';
  }

  function onSquareClick(sq: Square) {
    if (phantomDrag.shouldSuppressClick(sq)) return;
    const piece = pieces[sq];

    // If a piece is touched (server-authoritative), only commits to that square's destinations are valid.
    if (touchedSquare) {
      if (sq === touchedSquare) return; // tap own piece is no-op
      onCommit(touchedSquare, sq);
      return;
    }

    // If a piece is armed locally (no commit yet), clicking another own piece reassigns,
    // clicking elsewhere commits the armed piece to that destination.
    if (armedSquare) {
      if (piece?.color === you) {
        onArm(sq); // reassign
        return;
      }
      onCommit(armedSquare, sq); // commit to destination
      return;
    }

    // No arm yet. Tap an own piece to arm it.
    if (piece?.color === you) {
      onArm(sq);
    }
  }
</script>

<div class="board" class:flipped={you === 'b'}>
  {#each ranksDisplay as r (r)}
    {#each filesDisplay as f (f)}
      {@const sq = `${f}${r}` as Square}
      {@const piece = pieces[sq]}
      {@const ph = phantomsEnabled ? phantoms[sq] : undefined}
      {@const sc = squareColor(sq)}
      {@const isArmed = sq === armedSquare}
      {@const isTouched = sq === touchedSquare}
      {@const isHighlight = highlights.has(sq)}
      {@const isHighlightCap = isHighlight && piece && piece.color !== you}
      <button
        type="button"
        class="sq sq-{sc}"
        class:armed={isArmed}
        class:touched={isTouched}
        class:hl={isHighlight && !isHighlightCap}
        class:hl-cap={isHighlightCap}
        onclick={() => onSquareClick(sq)}
        aria-label={sq}
        data-square={sq}
      >
        {#if r === (you === 'w' ? '1' : '8') && f === filesDisplay[0]}
          <span class="coord coord-rank">{r}</span>
        {/if}
        {#if r === (you === 'w' ? '1' : '8')}
          <span class="coord coord-file">{f}</span>
        {/if}
        {#if piece}
          <span class="piece piece-{piece.color}">{pieceGlyph(piece)}</span>
        {/if}
        {#if ph && !piece}
          <!-- Phantom pieces are a pointer-only private annotation overlay;
               there is no keyboard drag interaction. The real game stays fully
               keyboard-operable via the square button below. -->
          <!-- svelte-ignore a11y_no_static_element_interactions -->
          <span
            class="phantom phantom-{ph.color}"
            class:dragging={sq === dragOrigin}
            onpointerdown={(e) => { e.preventDefault(); phantomDrag.start({ kind: 'board', from: sq, type: ph.type }, e); }}
          >{pieceGlyph(ph)}</span>
        {/if}
        {#if isHighlight && !piece}
          <span class="dot"></span>
        {/if}
      </button>
    {/each}
  {/each}
</div>

<style>
  .board {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    grid-template-rows: repeat(8, 1fr);
    width: 100%;
    aspect-ratio: 1 / 1;
    max-width: min(85vh, 92vw);
    margin: 0 auto;
    border: 2px solid var(--border);
    border-radius: 8px;
    overflow: hidden;
    user-select: none;
  }

  .sq {
    position: relative;
    border: none;
    border-radius: 0;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: clamp(28px, 5.5vw, 56px);
    line-height: 1;
    background: transparent;
    transition: background 0.1s;
  }
  .sq-light { background: var(--light); }
  .sq-dark { background: var(--dark); }
  .sq:hover { filter: brightness(1.1); }

  .piece {
    pointer-events: none;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  }
  .piece-w { color: #fafafa; }
  .piece-b { color: #1a1a1a; }

  .phantom {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: auto;
    touch-action: none;
    cursor: grab;
    opacity: 0.4;
    outline: 2px dashed var(--text-dim);
    outline-offset: -5px;
  }
  .phantom-w { color: #fafafa; }
  .phantom-b { color: #1a1a1a; }
  .phantom.dragging { opacity: 0.12; }

  .armed { box-shadow: inset 0 0 0 4px var(--armed); }
  .touched { box-shadow: inset 0 0 0 4px var(--touched); }
  .hl::before {
    content: '';
    position: absolute; inset: 0;
    background: radial-gradient(circle, var(--highlight) 18%, transparent 22%);
    pointer-events: none;
  }
  .hl-cap { box-shadow: inset 0 0 0 4px var(--highlight-cap); }

  .dot { display: none; } /* using ::before above */

  .coord {
    position: absolute;
    font-size: 11px;
    font-weight: 600;
    color: rgba(0,0,0,0.4);
    pointer-events: none;
  }
  .coord-rank { left: 3px; top: 3px; }
  .coord-file { right: 3px; bottom: 1px; }
  .sq-dark .coord { color: rgba(255,255,255,0.5); }
</style>
